/**
 * Generated by orval v8.5.3 🍺
 * Do not edit manually.
 * Metapic Social Media API Documentation
 * <p>Welcome to the API documentation for integrating with Metapic's Social Media Platform.</p>
            <h2>Authentication</h2>
            <p>This API uses <strong>Bearer Token</strong> authentication. You must include a valid token in the <strong>Authorization</strong> header with every request to access protected endpoints.</p>
            <h4>Example:</h4>
            <pre>Authorization: Bearer <i>your_token_here</i></pre>
            <p>*Replace <i>your_token_here</i> with your actual Bearer token. All API requests require this token for authentication.</p>
 * OpenAPI spec version: 1.0
 */
import * as axios from 'axios'
import type { AxiosInstance, AxiosRequestConfig, AxiosResponse } from 'axios'

import type {
  AddSegmentMembersDto,
  AdvertiserListSegments200,
  AdvertiserListSegmentsParams,
  AdvertiserPreview200,
  AdvertiserPreviewParams,
  CreateCreatorSegmentDto,
  CreatorSegment,
  ListMembers200,
  ListMembersParams,
  ListSegments200,
  ListSegmentsParams,
  Preview200,
  PreviewCreatorSegmentDto,
  PreviewParams,
  UpdateCreatorSegmentDto,
} from '../metapicSocialMediaAPIDocumentation.schemas'

export const getCreatorSegments = (axiosInstance: AxiosInstance = axios.default) => {
  /**
   * Writes the segment and its overrides, then starts a refresh workflow. Membership catches up behind the response.
   * @summary Create a creator segment
   */
  const create = (
    createCreatorSegmentDto: CreateCreatorSegmentDto,
    options?: AxiosRequestConfig,
  ): Promise<AxiosResponse<CreatorSegment>> => {
    return axiosInstance.post(`/v1/creator-segments`, createCreatorSegmentDto, options)
  }
  /**
   * @summary List creator segments
   */
  const listSegments = (
    params?: ListSegmentsParams,
    options?: AxiosRequestConfig,
  ): Promise<AxiosResponse<ListSegments200>> => {
    return axiosInstance.get(`/v1/creator-segments`, {
      ...options,
      params: { ...params, ...options?.params },
    })
  }
  /**
   * Merges rule matches with the draft manual members and exclusions, marking each row with its source. With a segment_id the rule block is the saved static segment's stored membership instead.
   * @summary Preview a criteria set without saving
   */
  const preview = (
    previewCreatorSegmentDto: PreviewCreatorSegmentDto,
    params?: PreviewParams,
    options?: AxiosRequestConfig,
  ): Promise<AxiosResponse<Preview200>> => {
    return axiosInstance.post(`/v1/creator-segments/preview`, previewCreatorSegmentDto, {
      ...options,
      params: { ...params, ...options?.params },
    })
  }
  /**
   * @summary Get a creator segment
   */
  const findOne = (
    id: string,
    options?: AxiosRequestConfig,
  ): Promise<AxiosResponse<CreatorSegment>> => {
    return axiosInstance.get(`/v1/creator-segments/${id}`, options)
  }
  /**
   * @summary Update a creator segment
   */
  const update = (
    id: string,
    updateCreatorSegmentDto: UpdateCreatorSegmentDto,
    options?: AxiosRequestConfig,
  ): Promise<AxiosResponse<CreatorSegment>> => {
    return axiosInstance.patch(`/v1/creator-segments/${id}`, updateCreatorSegmentDto, options)
  }
  /**
   * A segment targeted by a campaign that has not ended cannot be deleted, whether that campaign is running, scheduled or paused.
   * @summary Soft delete a creator segment
   */
  const remove = (id: string, options?: AxiosRequestConfig): Promise<AxiosResponse<void>> => {
    return axiosInstance.delete(`/v1/creator-segments/${id}`, options)
  }
  /**
   * @summary List current members of a segment
   */
  const listMembers = (
    id: string,
    params?: ListMembersParams,
    options?: AxiosRequestConfig,
  ): Promise<AxiosResponse<ListMembers200>> => {
    return axiosInstance.get(`/v1/creator-segments/${id}/members`, {
      ...options,
      params: { ...params, ...options?.params },
    })
  }
  /**
   * An existing exclusion for the creator is dropped: asking explicitly overrides it.
   * @summary Add creators to a saved segment
   */
  const addMembers = (
    id: string,
    addSegmentMembersDto: AddSegmentMembersDto,
    options?: AxiosRequestConfig,
  ): Promise<AxiosResponse<void>> => {
    return axiosInstance.post(`/v1/creator-segments/${id}/members`, addSegmentMembersDto, options)
  }
  /**
   * Removing a rule member writes an exclusion, otherwise the next refresh would re-add them.
   * @summary Remove one creator from a segment
   */
  const removeMember = (
    id: string,
    userId: string,
    options?: AxiosRequestConfig,
  ): Promise<AxiosResponse<void>> => {
    return axiosInstance.delete(`/v1/creator-segments/${id}/members/${userId}`, options)
  }
  /**
   * Re-evaluates a dynamic segment, or seeds a static one that has not been seeded yet. A static segment that already holds its members is curated by hand from then on.
   * @summary Refresh one segment now
   */
  const refresh = (id: string, options?: AxiosRequestConfig): Promise<AxiosResponse<void>> => {
    return axiosInstance.post(`/v1/creator-segments/${id}/refresh`, undefined, options)
  }
  /**
   * The path fixes the scope, and the body carries no advertiser of its own. Writes the segment and its overrides, then starts a refresh workflow.
   * @summary Create a segment for one advertiser
   */
  const advertiserCreate = (
    advertiserId: number,
    createCreatorSegmentDto: CreateCreatorSegmentDto,
    options?: AxiosRequestConfig,
  ): Promise<AxiosResponse<CreatorSegment>> => {
    return axiosInstance.post(
      `/v1/advertisers/${advertiserId}/creator-segments`,
      createCreatorSegmentDto,
      options,
    )
  }
  /**
   * @summary List one advertiser's creator segments
   */
  const advertiserListSegments = (
    advertiserId: number,
    params?: AdvertiserListSegmentsParams,
    options?: AxiosRequestConfig,
  ): Promise<AxiosResponse<AdvertiserListSegments200>> => {
    return axiosInstance.get(`/v1/advertisers/${advertiserId}/creator-segments`, {
      ...options,
      params: { ...params, ...options?.params },
    })
  }
  /**
   * Merges rule matches with the draft manual members and exclusions, marking each row with its source. With a segment_id the rule block is the saved static segment's stored membership instead.
   * @summary Preview a criteria set without saving
   */
  const advertiserPreview = (
    advertiserId: number,
    previewCreatorSegmentDto: PreviewCreatorSegmentDto,
    params?: AdvertiserPreviewParams,
    options?: AxiosRequestConfig,
  ): Promise<AxiosResponse<AdvertiserPreview200>> => {
    return axiosInstance.post(
      `/v1/advertisers/${advertiserId}/creator-segments/preview`,
      previewCreatorSegmentDto,
      {
        ...options,
        params: { ...params, ...options?.params },
      },
    )
  }
  return {
    create,
    listSegments,
    preview,
    findOne,
    update,
    remove,
    listMembers,
    addMembers,
    removeMember,
    refresh,
    advertiserCreate,
    advertiserListSegments,
    advertiserPreview,
  }
}
export type CreateResult = AxiosResponse<CreatorSegment>
export type ListSegmentsResult = AxiosResponse<ListSegments200>
export type PreviewResult = AxiosResponse<Preview200>
export type FindOneResult = AxiosResponse<CreatorSegment>
export type UpdateResult = AxiosResponse<CreatorSegment>
export type RemoveResult = AxiosResponse<void>
export type ListMembersResult = AxiosResponse<ListMembers200>
export type AddMembersResult = AxiosResponse<void>
export type RemoveMemberResult = AxiosResponse<void>
export type RefreshResult = AxiosResponse<void>
export type AdvertiserCreateResult = AxiosResponse<CreatorSegment>
export type AdvertiserListSegmentsResult = AxiosResponse<AdvertiserListSegments200>
export type AdvertiserPreviewResult = AxiosResponse<AdvertiserPreview200>
